/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { PlatformAdminUIState, PlatformAdminUIThunkType } from '../../../../store/store';
import {
	showSnackbar,
	SnackbarAction,
	SnackbarType
} from '../../../../shared/views/SnackbarNotification/store/SnackbarNotification.actions';
import { UserActionTypes } from '../../store/UserActionTypes';
import { ActionMenuItemProps } from '../../../../shared/views/ActionMenu/ActionMenu';
import {
	ClientPersonChangeStatusRequest,
	ClientPersonChangeStatusRequestStatusesToAddEnum,
	ClientPersonChangeStatusRequestStatusesToRemoveEnum,
	ClientPersonExtended,
	ClientPersonExtendedStatusesEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { changeStatusByAdmin } from '../../store/User.actions';
import ChangeStatusActionView from './ChangeStatusAction.view';

interface Props extends ActionMenuItemProps {
	user: ClientPersonExtended | null;
	showSnackbar: (snackbarAction: SnackbarAction) => void;
	changeStatus: (changeStatusRequestParams: ClientPersonChangeStatusRequest) => void;
}

class ChangeStatusActionContainer extends Component<Props> {
	constructor(props: Props) {
		super(props);
		this.handleSave = this.handleSave.bind(this);
	}

	isUserInParallelWorld(user: ClientPersonExtended): boolean {
		return !!user?.statuses?.has(ClientPersonExtendedStatusesEnum.ParallelWorldInhabitant);
	}

	handleSave(): void {
		const { user, showSnackbar, changeStatus } = this.props;
		const personId = user?.id;
		if (user && personId) {
			changeStatus({
				personId,
				statusesToAdd: new Set(
					!this.isUserInParallelWorld(user)
						? [ClientPersonChangeStatusRequestStatusesToAddEnum.ParallelWorldInhabitant]
						: []
				),
				statusesToRemove: new Set(
					this.isUserInParallelWorld(user)
						? [ClientPersonChangeStatusRequestStatusesToRemoveEnum.ParallelWorldInhabitant]
						: []
				)
			});
		} else {
			showSnackbar({
				type: SnackbarType.SNACKBAR_ERROR,
				message: 'Benutzer hat keine ID.'
			});
		}
	}

	render(): JSX.Element | null {
		const { user, renderMenuItem } = this.props;
		if (!user || user?.deleted) {
			return null;
		}
		const isInParallelWorld = this.isUserInParallelWorld(user);
		const actionText = isInParallelWorld ? 'In richtige Welt holen' : 'In die Parallelwelt schicken';

		return (
			<ChangeStatusActionView
				text={actionText}
				userFirstName={user.firstName ?? ''}
				isInParallelWorld={isInParallelWorld}
				renderMenuItem={renderMenuItem}
				handleSave={this.handleSave}
			/>
		);
	}
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapStateToProps = (state: PlatformAdminUIState) => {
	return {
		user: state.user.detail.user
	};
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapDispatchToProps = (dispatch: PlatformAdminUIThunkType<UserActionTypes>) => {
	return {
		showSnackbar: (snackbarAction: SnackbarAction): Promise<void> => dispatch(showSnackbar(snackbarAction)),
		changeStatus: (changeStatusRequestParams: ClientPersonChangeStatusRequest): Promise<void> =>
			dispatch(changeStatusByAdmin(changeStatusRequestParams))
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(ChangeStatusActionContainer);
